import type { ChartConfig, Entitlements, OptionSchema } from '@chartforge/shared-types';
import {
  CONFIG_GROUPS,
  filterSchemasByEntitlement,
  getOptionValue,
  updateConfig
} from '@chartforge/engine-core';
import { controlForOptionType, type ControlProps } from './controls/registry';

export interface SchemaFormProps {
  /** All option schemas of the active chart plugin. */
  schemas: OptionSchema[];
  config: ChartConfig;
  /** When provided, options above the plan tier are filtered out centrally. */
  entitlements?: Entitlements;
  onChange: (config: ChartConfig) => void;
  disabled?: boolean;
}

const SECTION_TITLES: Record<string, string> = {
  appearance: 'Appearance & Fonts',
  layout: 'Layout & Spacing',
  axes: 'Axes & Gridlines',
  labels: 'Labels & Tooltips',
  data: 'Data & Scale'
};

/**
 * Generic schema-driven settings form. It renders whatever the schema
 * declares — grouped into ChartConfig buckets, filtered by entitlements —
 * without any chart-specific code.
 */
export function SchemaForm({ schemas, config, entitlements, onChange, disabled }: SchemaFormProps) {
  const visible = entitlements ? filterSchemasByEntitlement(schemas, entitlements) : schemas;

  const sections = CONFIG_GROUPS.map((group) => ({
    group,
    options: visible.filter((s) => s.group === group)
  })).filter((section) => section.options.length > 0);

  return (
    <div className="cf-form">
      {sections.map((section) => (
        <section key={section.group} className="cf-form-section" data-group={section.group}>
          <h4>{SECTION_TITLES[section.group] ?? section.group}</h4>
          {section.options.map((schema) => {
            const Control = controlForOptionType(schema.type);
            const controlProps: ControlProps = {
              schema,
              value: getOptionValue(config, schema),
              disabled,
              onChange: (value) => onChange(updateConfig(config, schema, value))
            };
            return (
              <div key={schema.id} className="cf-form-row" data-option-id={schema.id}>
                <label htmlFor={`cf-opt-${schema.id}`}>{schema.label}</label>
                <Control {...controlProps} />
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}
